import { useEffect, useMemo, useState } from 'react'
import { Download } from '@nine-thirty-five/material-symbols-react/sharp'
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  LinkText,
  TextField,
  Typography
} from '@iziplay/ui-kit'

import Spinner from '../components/spinner'
import { MarqueeText } from '../components/marqueeText'
import { setCurrentPath } from '../state/ui'
import {
  AvailableBook,
  bookCoverByAnnaIDURL,
  problemDetail,
  useAvailableBooks
} from '../queries/books'
import { matchesCatalogQuery } from '../tools/isbn'

// The catalog's cover, by Anna record id: the books service extracts it
// from the EPUB it has on the server -- the exact file the EPUB button
// downloads -- and falls back to the ISBN flow when it has none. The same
// placeholder as the library cards when there is no cover at all.
function CatalogCover({ book }: { book: AvailableBook }) {
  const [failed, setFailed] = useState(false)

  if (failed) {
    return (
      <CardMedia
        style={{
          aspectRatio: '2 / 3',
          backgroundColor: 'var(--black)',
          backgroundImage: "url('/img/white-50.png')",
          backgroundSize: '160%',
          backgroundPosition: 'center'
        }}
        title={book.title}
      />
    )
  }

  return (
    <CardMedia
      component="img"
      image={bookCoverByAnnaIDURL(book.id)}
      alt={book.title}
      style={{ aspectRatio: '2 / 3', objectFit: 'cover' }}
      onError={() => setFailed(true)}
    />
  )
}

function AvailableBookCard({ book }: { book: AvailableBook }) {
  const [hovered, setHovered] = useState(false)

  return (
    <Card
      variant="outlined"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <CatalogCover book={book} />
      <CardContent>
        <Typography variant="body1" component="h2">
          <MarqueeText text={book.title} hovered={hovered} />
        </Typography>
        <Typography variant="body2" component="p" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {book.author}
        </Typography>
        {book.year ? (
          <Typography variant="body2" color="textSecondary" component="p" style={{ opacity: 0.55 }}>
            {book.year}
          </Typography>
        ) : null}
        {book.languages.length > 0 && (
          <Typography variant="body2" color="textSecondary" component="p" style={{ opacity: 0.55 }}>
            {book.languages.join(', ')}
          </Typography>
        )}
      </CardContent>
      <CardActions>
        {book.url && (
          <Button href={book.url} variant="text" startIcon={<Download size={20} />}>
            EPUB
          </Button>
        )}
      </CardActions>
    </Card>
  )
}

/*
 * The catalog of books the server already has on disk (the books service's
 * /v1/downloads, proxied from Anna's). Not on the sidebar: it is reached
 * from the my-library page, which the sidebar's Books entry points at.
 */
export function Books() {
  const books = useAvailableBooks()
  const [query, setQuery] = useState('')

  useEffect(() => {
    setCurrentPath(['Books'])
  }, [])

  const results = useMemo(
    () => (books.data?.results ?? []).filter(book => matchesCatalogQuery(book, query)),
    [books.data, query]
  )

  if (books.isPending) {
    return <Spinner />
  }

  return (
    <div className="scrollable" style={{ paddingBottom: '12px' }}>
      <Card variant="outlined" style={{ marginBottom: '12px' }}>
        <CardContent>
          The books the server already has, ready to download. Search by title, author or ISBN
          (10 or 13 digits). Your own shelf lives in{' '}
          <LinkText to="/books/my-library">My library</LinkText>.
        </CardContent>
        <CardActions>
          <div style={{ display: 'flex', flex: 1 }}>
            <TextField
              id="book-search"
              placeholder="Title, author or ISBN"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
        </CardActions>
      </Card>
      {books.error ? (
        <Card variant="outlined">
          <CardContent>
            Failed to load the available books: {problemDetail(books.error, 'try again in a moment')}
          </CardContent>
        </Card>
      ) : (
        <>
          <Typography variant="body2" component="p" style={{ padding: '0 12px', opacity: 0.55 }}>
            {query.trim() === ''
              ? `${results.length} ${results.length === 1 ? 'book' : 'books'} available on the server`
              : `${results.length} of ${books.data?.total ?? results.length} books match "${query.trim()}"`}
          </Typography>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px', padding: '12px', boxSizing: 'border-box' }}>
            {results.map(book => (
              <AvailableBookCard key={book.id} book={book} />
            ))}
          </div>
          {results.length === 0 && (
            <Card variant="outlined" style={{ margin: '0 12px' }}>
              <CardContent>No book matches &quot;{query.trim()}&quot;.</CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
